import type { ActionFlowTraceEntry } from '@/features/preview/types'

export type JsonEvidenceKind =
  | 'event'
  | 'flow'
  | 'state'
  | 'result'
  | 'computed'
  | 'datasource'
  | 'renderer'

export interface ActionFlowJsonEvidence {
  kind: JsonEvidenceKind
  label: string
  value: string
  path: Array<string | number>
  detail?: string
}

interface BlockMatch {
  blockName: string
  block: Record<string, unknown>
  flowName: string
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null
}

function asArray(value: unknown): unknown[] {
  return Array.isArray(value) ? value : []
}

function normalizeFlowName(flowName: string): string {
  return flowName.split('.').at(-1) ?? flowName
}

function getBlockFlows(block: Record<string, unknown>): Record<string, unknown> {
  const flow = asRecord(block.flow)
  return asRecord(flow?.flows) ?? {}
}

function flowExists(block: Record<string, unknown>, flowName: string): boolean {
  return Object.prototype.hasOwnProperty.call(getBlockFlows(block), flowName)
}

function findBlockMatch(projectJson: Record<string, unknown>, trace: ActionFlowTraceEntry): BlockMatch | null {
  const blocks = asRecord(projectJson.blocks) ?? {}
  const flowName = normalizeFlowName(trace.flowName)

  for (const [blockName, rawBlock] of Object.entries(blocks)) {
    const block = asRecord(rawBlock)
    if (!block) continue

    const bindings = asRecord(asRecord(block.events)?.bindings) ?? {}
    const binding = asRecord(bindings[trace.eventLabel])
    if (binding?.flow === flowName || binding?.flow === trace.flowName) {
      return { blockName, block, flowName }
    }
  }

  for (const [blockName, rawBlock] of Object.entries(blocks)) {
    const block = asRecord(rawBlock)
    if (block && flowExists(block, flowName)) {
      return { blockName, block, flowName }
    }
  }

  return null
}

function statePath(blockName: string, diffPath: string): Array<string | number> | null {
  const parts = diffPath.split('.').filter(Boolean)
  const [scope, ...tail] = parts
  if (!scope || tail.length === 0) return null

  if (scope === 'local') {
    return ['blocks', blockName, 'state', 'localState', ...tail]
  }
  if (scope === 'appData') {
    return ['manifest', 'appData', ...tail]
  }
  if (scope === 'computed') {
    return ['blocks', blockName, 'state', 'computed', ...tail]
  }
  return null
}

function resultStepPath(
  blockName: string,
  block: Record<string, unknown>,
  flowName: string,
  resultName: string,
): Array<string | number> {
  const flow = asRecord(getBlockFlows(block)[flowName])
  const steps = asArray(flow?.steps)
  const stepIndex = steps.findIndex((step) => asRecord(step)?.name === resultName)
  if (stepIndex >= 0) {
    return ['blocks', blockName, 'flow', 'flows', flowName, 'steps', stepIndex]
  }
  return ['blocks', blockName, 'flow', 'flows', flowName]
}

function actionCallPath(
  blockName: string,
  flowName: string,
  callPath: string,
): Array<string | number> {
  const parts: Array<string | number> = ['blocks', blockName, 'flow', 'flows', flowName]
  for (const segment of callPath.replace(/\[(\d+)\]/g, '.$1').split('.')) {
    if (!segment) continue
    if (/^\d+$/.test(segment)) parts.push(Number(segment))
    else if (/^[A-Za-z_$][\w$]*$/.test(segment)) parts.push(segment)
  }
  return parts
}

function getNamedResultSteps(block: Record<string, unknown>, flowName: string): string[] {
  const flow = asRecord(getBlockFlows(block)[flowName])
  return asArray(flow?.steps)
    .map((step) => asRecord(step)?.name)
    .filter((name): name is string => typeof name === 'string' && name.length > 0)
}

function findUiNodePath(
  node: unknown,
  elementId: string,
  basePath: Array<string | number>,
): Array<string | number> | null {
  const record = asRecord(node)
  if (!record) return null
  if (record.id === elementId) return basePath

  const children = asArray(record.children)
  for (let index = 0; index < children.length; index += 1) {
    const match = findUiNodePath(children[index], elementId, [...basePath, 'children', index])
    if (match) return match
  }
  return null
}

function rendererPath(
  blockName: string,
  block: Record<string, unknown>,
  elementId: string,
): Array<string | number> {
  return (
    findUiNodePath(asRecord(block.ui)?.root, elementId, ['blocks', blockName, 'ui', 'root']) ?? [
      'blocks',
      blockName,
      'ui',
      'root',
    ]
  )
}

function pushUnique(rows: ActionFlowJsonEvidence[], row: ActionFlowJsonEvidence) {
  const key = `${row.kind}:${row.label}:${formatJsonPath(row.path)}`
  if (!rows.some((item) => `${item.kind}:${item.label}:${formatJsonPath(item.path)}` === key)) {
    rows.push(row)
  }
}

export function buildActionFlowJsonEvidence(
  projectJson: Record<string, unknown>,
  trace: ActionFlowTraceEntry | null | undefined,
): ActionFlowJsonEvidence[] {
  if (!trace) return []

  const match = findBlockMatch(projectJson, trace)
  if (!match) return []

  const rows: ActionFlowJsonEvidence[] = []
  const { blockName, block, flowName } = match

  pushUnique(rows, {
    kind: 'event',
    label: 'Event binding',
    value: trace.eventLabel,
    path: ['blocks', blockName, 'events', 'bindings', trace.eventLabel],
    detail: 'UI event resolves to the declared ActionFlow.',
  })

  pushUnique(rows, {
    kind: 'flow',
    label: 'ActionFlow',
    value: flowName,
    path: ['blocks', blockName, 'flow', 'flows', flowName],
    detail: `${trace.status} in ${trace.durationMs}ms`,
  })

  const declaredResultNames = getNamedResultSteps(block, flowName)
  for (const resultName of declaredResultNames) {
    pushUnique(rows, {
      kind: 'result',
      label: '_results trace',
      value: `_results.${resultName}`,
      path: resultStepPath(blockName, block, flowName, resultName),
      detail: Object.prototype.hasOwnProperty.call(trace.results, resultName)
        ? formatCompactValue(trace.results[resultName])
        : 'declared compute step',
    })
  }

  for (const resultName of Object.keys(trace.results)) {
    if (declaredResultNames.length > 0 && !declaredResultNames.includes(resultName)) continue
    pushUnique(rows, {
      kind: 'result',
      label: '_results trace',
      value: `_results.${resultName}`,
      path: resultStepPath(blockName, block, flowName, resultName),
      detail: formatCompactValue(trace.results[resultName]),
    })
  }

  for (const computedName of Object.keys(trace.computed)) {
    pushUnique(rows, {
      kind: 'computed',
      label: 'Computed projection',
      value: `computed.${computedName}`,
      path: ['blocks', blockName, 'state', 'computed', computedName],
      detail: formatCompactValue(trace.computed[computedName]),
    })
  }

  for (const call of trace.actionCalls) {
    if (call.dataSource !== 'local_db' && call.dataSource !== 'external') continue
    pushUnique(rows, {
      kind: 'datasource',
      label: call.dataSource === 'local_db' ? 'Local DB write' : 'External datasource',
      value: call.target ?? call.action,
      path: actionCallPath(blockName, flowName, call.path),
      detail: call.operation
        ? `${call.dataSource === 'external' ? 'method' : 'operation'}: ${call.operation}`
        : call.dataSource === 'external'
          ? 'external datasource action'
          : 'local datasource action',
    })
  }

  const computedValues = new Set(Object.values(trace.computed).map((value) => formatCompactValue(value)))
  const sortedProjections = [...trace.rendererProjection].sort((left, right) => {
    const leftScore = computedValues.has(left.text) ? 0 : 1
    const rightScore = computedValues.has(right.text) ? 0 : 1
    return leftScore - rightScore
  })

  for (const projection of sortedProjections) {
    pushUnique(rows, {
      kind: 'renderer',
      label: 'Renderer text',
      value: projection.elementId,
      path: rendererPath(blockName, block, projection.elementId),
      detail: projection.text,
    })
  }

  for (const diff of trace.stateDiffs) {
    const path = statePath(blockName, diff.path)
    if (!path) continue
    if (diff.path.startsWith('computed.')) {
      const computedName = diff.path.slice('computed.'.length)
      if (Object.prototype.hasOwnProperty.call(trace.computed, computedName)) continue
    }
    pushUnique(rows, {
      kind: diff.path.startsWith('computed.') ? 'computed' : 'state',
      label: diff.path.startsWith('appData.') ? 'App data write' : 'State write',
      value: diff.path,
      path,
      detail: `${formatCompactValue(diff.before)} -> ${formatCompactValue(diff.after)}`,
    })
  }

  return rows
}

export function formatJsonPath(path: readonly (string | number)[]): string {
  return path.reduce<string>((acc, segment) => {
    if (typeof segment === 'number') return `${acc}[${segment}]`
    return /^[A-Za-z_$][\w$]*$/.test(segment)
      ? `${acc}.${segment}`
      : `${acc}[${JSON.stringify(segment)}]`
  }, '$')
}

function formatCompactValue(value: unknown): string {
  if (value === undefined) return 'undefined'
  if (typeof value === 'string') return value
  try {
    return JSON.stringify(value)
  } catch {
    return String(value)
  }
}
